import React, { useState, useRef } from 'react';
import { Upload, Check, AlertCircle, Loader } from 'lucide-react';
import { API_BASE_URL } from '../config';
import * as XLSX from 'xlsx';

const CustomerImport = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [customerType, setCustomerType] = useState('jk_verkauft'); // Default: JK Verkauft
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  // Funzione per gestire il drag & drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Funzione per gestire la selezione del file
  const handleFileSelect = (file) => {
    // Verifica che sia un file CSV o Excel
    const isValidFile = file && (
      file.type === 'text/csv' || 
      file.name.endsWith('.csv') ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls')
    );
    
    if (isValidFile) {
      setSelectedFile(file);
      setUploadResult(null);
      setPreviewData(null);
      setShowPreview(false);
      
      // Genera un preview dei dati
      generatePreview(file);
    } else {
      alert('Bitte wählen Sie eine CSV- oder Excel-Datei aus.');
    }
  };

  // Funzione per parsare le date (replica della logica legacy)
  const parseContractDate = (dateValue) => {
    if (!dateValue) return null;
    
    // Converti in stringa per sicurezza
    const dateStr = String(dateValue).trim();
    if (!dateStr) return null;
    
    let contractDate = null;
    
    // Formato 1: YYYY-MM-DD (ISO)
    try {
      const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (isoMatch) {
        contractDate = new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
        if (!isNaN(contractDate.getTime())) return contractDate;
      }
    } catch (e) {}
    
    // Formato 2: DD.MM.YYYY (tedesco)
    try {
      const germanMatch = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (germanMatch) {
        contractDate = new Date(parseInt(germanMatch[3]), parseInt(germanMatch[2]) - 1, parseInt(germanMatch[1]));
        if (!isNaN(contractDate.getTime())) return contractDate;
      }
    } catch (e) {}
    
    // Formato 3: DD/MM/YYYY
    try {
      const slashMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (slashMatch) {
        contractDate = new Date(parseInt(slashMatch[3]), parseInt(slashMatch[2]) - 1, parseInt(slashMatch[1]));
        if (!isNaN(contractDate.getTime())) return contractDate;
      }
    } catch (e) {}
    
    // Formato 4: MM/DD/YYYY (americano)
    try {
      const usMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (usMatch) {
        contractDate = new Date(parseInt(usMatch[3]), parseInt(usMatch[1]) - 1, parseInt(usMatch[2]));
        if (!isNaN(contractDate.getTime())) return contractDate;
      }
    } catch (e) {}
    
    // Formato 5: Numero Excel (se è un numero)
    try {
      if (typeof dateValue === 'number' || (typeof dateValue === 'string' && !isNaN(parseFloat(dateValue)))) {
        const excelDate = parseFloat(dateValue);
        if (excelDate > 1 && excelDate < 100000) { // Range ragionevole per date Excel
          // Excel conta i giorni dal 1/1/1900, ma ha un bug: considera il 1900 bisestile
          let adjustedDate = excelDate;
          if (excelDate >= 60) {
            adjustedDate -= 1; // Corregge il bug di Excel
          }
          const baseDate = new Date(1900, 0, 1); // 1 gennaio 1900
          contractDate = new Date(baseDate.getTime() + (adjustedDate - 1) * 24 * 60 * 60 * 1000);
          if (!isNaN(contractDate.getTime())) return contractDate;
        }
      }
    } catch (e) {}
    
    // Formato 6: Se è già una data JavaScript
    if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      return dateValue;
    }
    
    // Ultimo tentativo: parsing generico
    try {
      contractDate = new Date(dateStr);
      if (!isNaN(contractDate.getTime())) return contractDate;
    } catch (e) {}
    
    return null;
  };

  // Funzione per generare un preview dei dati (CSV o Excel)
  const generatePreview = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let headers = [];
        let sampleRows = [];
        let totalRows = 0;
        
        if (file.name.endsWith('.csv')) {
          // Gestione CSV
          const csv = e.target.result;
          const lines = csv.split('\n').filter(line => line.trim());
          
          if (lines.length > 1) {
            headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
            sampleRows = lines.slice(1, 6).map(line => {
              const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
              const row = {};
              headers.forEach((header, index) => {
                if (header.toLowerCase().includes('contract_date') || header.toLowerCase().includes('vertragsdatum')) {
                  // Parsa la data usando la logica completa
                  row[header] = parseContractDate(values[index]);
                } else {
                  row[header] = values[index] || '';
                }
              });
              return row;
            });
            totalRows = lines.length - 1;
          }
        } else {
          // Gestione Excel
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length > 1) {
            headers = jsonData[0].map(String);
            sampleRows = jsonData.slice(1, 6).map(row => {
              const rowObj = {};
              headers.forEach((header, index) => {
                if (header.toLowerCase().includes('contract_date') || header.toLowerCase().includes('vertragsdatum')) {
                  // Parsa la data usando la logica completa
                  rowObj[header] = parseContractDate(row[index]);
                } else {
                  rowObj[header] = row[index] || '';
                }
              });
              return rowObj;
            });
            totalRows = jsonData.length - 1;
          }
        }
        
        setPreviewData({
          headers,
          rows: sampleRows,
          totalRows
        });
      } catch (error) {
        console.error('Errore nel parsing del file:', error);
      }
    };
    
    if (file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  // Funzione per l'upload del file
  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Bitte wählen Sie eine Datei aus.');
      return;
    }

    if (!customerType) {
      alert('Bitte wählen Sie einen Kundentyp aus.');
      return;
    }

    setIsUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      // Estrai il tipo e il dipartimento dal customerType
      const [department, type] = customerType.split('_');
      formData.append('customer_type', type); // verkauft, aftersales o fruhling
      formData.append('department', department); // jk, ahs o special

      const response = await fetch(`${API_BASE_URL}/customers/import-workflow/`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        setUploadResult({
          success: true,
          message: result.message,
          imported: result.imported || 0,
          errors: result.errors || [],
          warnings: result.warnings || []
        });
        
        // Reset del form dopo successo
        setSelectedFile(null);
        setPreviewData(null);
        setShowPreview(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setUploadResult({
          success: false,
          message: result.detail || result.message || 'Fehler beim Import der Kunden.',
          errors: result.errors || []
        });
      }
    } catch (error) {
      console.error('Errore durante l\'upload:', error);
      setUploadResult({
        success: false,
        message: 'Netzwerkfehler beim Import der Kunden.',
        errors: [error.message]
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Funzione per resettare il form
  const resetForm = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setShowPreview(false);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Upload className="h-5 w-5 mr-2 text-indigo-500" />
              Kunden importieren
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Importieren Sie neue Kunden aus CSV- oder Excel-Dateien direkt in die Workflow-Listen
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Selezione tipo cliente e dipartimento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Kundentyp auswählen <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">JK Listen</h4>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="customerType"
                      value="jk_verkauft"
                      checked={customerType === 'jk_verkauft'}
                      onChange={(e) => setCustomerType(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <span className="font-medium">JK Verkauft</span>
                      <span className="text-gray-500 block text-xs">Kunden für JK Verkaufskampagnen</span>
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="customerType"
                      value="jk_aftersales"
                      checked={customerType === 'jk_aftersales'}
                      onChange={(e) => setCustomerType(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <span className="font-medium">JK Aftersales</span>
                      <span className="text-gray-500 block text-xs">Kunden für JK Aftersales-Kampagnen</span>
                    </span>
                  </label>
                </div>
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">AHS Listen</h4>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="customerType"
                      value="ahs_verkauft"
                      checked={customerType === 'ahs_verkauft'}
                      onChange={(e) => setCustomerType(e.target.value)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <span className="font-medium">AHS Verkauft</span>
                      <span className="text-gray-500 block text-xs">Kunden für AHS Verkaufskampagnen</span>
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="customerType"
                      value="ahs_aftersales"
                      checked={customerType === 'ahs_aftersales'}
                      onChange={(e) => setCustomerType(e.target.value)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <span className="font-medium">AHS Aftersales</span>
                      <span className="text-gray-500 block text-xs">Kunden für AHS Aftersales-Kampagnen</span>
                    </span>
                  </label>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                <h4 className="text-sm font-medium text-gray-900">Special</h4>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="customerType"
                    value="special_fruhling"
                    checked={customerType === 'special_fruhling'}
                    onChange={(e) => setCustomerType(e.target.value)}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <span className="font-medium">Kostenlose Fruhling Check</span>
                    <span className="text-gray-500 block text-xs">Kunden für die Special-Kampagne</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Area upload file */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Datei auswählen <span className="text-red-500">*</span>
              </label>
              
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-400 transition-colors"
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                  className="hidden"
                />
                
                {!selectedFile ? (
                  <div>
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-sm text-gray-600 mb-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-indigo-600 hover:text-indigo-500 font-medium"
                      >
                        Datei auswählen
                      </button>
                      {' '}oder per Drag & Drop hierher ziehen
                    </p>
                      <p className="text-xs text-gray-500">CSV, Excel (.xlsx, .xls) Dateien bis 10MB sind erlaubt</p>
                  </div>
                ) : (
                  <div>
                    <Check className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <p className="text-sm text-gray-900 font-medium mb-1">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500 mb-4">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="text-sm text-red-600 hover:text-red-500 font-medium"
                    >
                      Datei entfernen
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Preview dei dati */}
            {previewData && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Datenvorschau ({previewData.totalRows} Datensätze gefunden)
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="text-sm text-indigo-600 hover:text-indigo-500 font-medium"
                  >
                    {showPreview ? 'Ausblenden' : 'Anzeigen'}
                  </button>
                </div>
                
                {showPreview && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Name/Vorname
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Telefon
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Vertragsdatum
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {previewData.rows.map((row, rowIndex) => {
                            // Trova le colonne essenziali
                            const nameHeader = previewData.headers.find(h => 
                              h.toLowerCase().includes('name') || h.toLowerCase().includes('vorname')
                            );
                            const phoneHeader = previewData.headers.find(h => 
                              h.toLowerCase().includes('phone') || h.toLowerCase().includes('telefon')
                            );
                            const dateHeader = previewData.headers.find(h => 
                              h.toLowerCase().includes('contract_date') || h.toLowerCase().includes('vertragsdatum')
                            );
                            
                            return (
                              <tr key={rowIndex}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {row[nameHeader] || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {row[phoneHeader] || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {row[dateHeader] instanceof Date 
                                    ? row[dateHeader].toLocaleDateString('de-DE') 
                                    : (row[dateHeader] || '-')
                                  }
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-gray-50 px-6 py-3">
                      <p className="text-xs text-gray-500">
                        Zeigt die ersten 5 Datensätze von {previewData.totalRows} insgesamt
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pulsante import */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                {selectedFile && customerType && (
                  <span>
                    Bereit zum Import in <strong>{
                      customerType === 'jk_verkauft' ? 'JK Verkauft' :
                      customerType === 'jk_aftersales' ? 'JK Aftersales' :
                      customerType === 'ahs_verkauft' ? 'AHS Verkauft' :
                      customerType === 'ahs_aftersales' ? 'AHS Aftersales' :
                      customerType === 'special_fruhling' ? 'Kostenlose Fruhling Check' : customerType
                    }</strong>
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleUpload}
                disabled={!selectedFile || !customerType || isUploading}
                className={`inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white
                  ${(!selectedFile || !customerType || isUploading) 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                  }`}
              >
                {isUploading ? (
                  <>
                    <Loader className="animate-spin h-4 w-4 mr-2" />
                    Importiere...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Kunden importieren
                  </>
                )}
              </button>
            </div>
          </div>

        {/* Risultati dell'upload */}
        {uploadResult && (
          <div className={`mx-6 mt-6 mb-6 rounded-lg p-6 ${
            uploadResult.success 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start">
              {uploadResult.success ? (
                <Check className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
              )}
              <div className="flex-grow">
                <h3 className={`text-sm font-medium ${
                  uploadResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {uploadResult.success ? 'Import erfolgreich!' : 'Import fehlgeschlagen'}
                </h3>
                <p className={`text-sm mt-1 ${
                  uploadResult.success ? 'text-green-700' : 'text-red-700'
                }`}>
                  {uploadResult.message}
                </p>
                
                {uploadResult.imported && uploadResult.imported > 0 && (
                <p className="text-sm text-green-700 mt-2">
                  <strong>{uploadResult.imported}</strong> Kunden erfolgreich in <strong>{
                    customerType === 'jk_verkauft' ? 'JK Verkauft' :
                    customerType === 'jk_aftersales' ? 'JK Aftersales' :
                    customerType === 'ahs_verkauft' ? 'AHS Verkauft' :
                    customerType === 'ahs_aftersales' ? 'AHS Aftersales' :
                    customerType === 'special_fruhling' ? 'Kostenlose Fruhling Check' : customerType
                  }</strong> importiert.
                </p>
                )}

                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-medium text-red-800">Fehler:</h4>
                    <ul className="text-sm text-red-700 mt-1 space-y-1">
                      {uploadResult.errors.map((error, index) => (
                        <li key={index} className="flex items-start">
                          <span className="inline-block w-1 h-1 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {uploadResult.warnings && uploadResult.warnings.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-medium text-yellow-800">Warnungen:</h4>
                    <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                      {uploadResult.warnings.map((warning, index) => (
                        <li key={index} className="flex items-start">
                          <span className="inline-block w-1 h-1 bg-yellow-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

    </div>
  );
};

export default CustomerImport;
